/* eslint-env browser */
// Users › Documents (wave 3, W2_Portal_BookingDetails look): the person's ID
// and, for drivers, the licence, ownership certificate and insurance, as
// cards with the picture; Edit turns them into upload tiles; a picture opens
// large with zoom and drag.
//
// Data, exactly as before: the user from the list already loaded
// (fetchUsersOnce on arrival; an unknown id goes to /404); the pictures shown
// through the secure document links (getSecureDocumentUrl, one per document
// on file), falling back to the stored address. Save (critical edits on;
// admin and call centre only) uploads each chosen file with
// updateLicenseImage(uid, file, field), all awaited before the page settles
// (so the lists see the real uploaded addresses), then leaves edit mode.
// Back returns to the users tab with the page number.
import React, { useState, useEffect, useCallback, useContext, useMemo, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { api, FirebaseContext, store } from 'common';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import ZoomInRoundedIcon from '@mui/icons-material/ZoomInRounded';
import ZoomOutRoundedIcon from '@mui/icons-material/ZoomOutRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ImageNotSupportedOutlinedIcon from '@mui/icons-material/ImageNotSupportedOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import { tokens as c } from '../theme';
import { showToast, InlineMessage } from '../components/feedback';
import { BackButton, PageHeader, IconCircle, calmCaps, goBackOr } from '../components/modern';
import { getSecureDocumentUrl, extractStoragePath } from '../utils/securityUtils';

const NO_IMAGES = { verifyIdImage: null, licenseImage: null, licenseImageBack: null, insuranceImage: null };
const MAX_ZOOM = 3;
const MIN_ZOOM = 0.5;

function UserDocuments() {
  const { id, rId } = useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { state } = useLocation();
  const dispatch = useDispatch();
  const { auth: firebaseAuth, config } = useContext(FirebaseContext);
  const settings = useSelector((state) => state.settingsdata.settings);
  const staticusers = useSelector((state) => state.usersdata.staticusers);
  const { fetchUsersOnce, updateLicenseImage } = api;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editable, setEditable] = useState(false);
  const [images, setImages] = useState(NO_IMAGES);
  const [formError, setFormError] = useState(null);
  const [secureUrls, setSecureUrls] = useState({});
  const [loadingUrls, setLoadingUrls] = useState(false);
  const [imageModal, setImageModal] = useState({ open: false, url: '', title: '' });
  const [zoomLevel, setZoomLevel] = useState(1);
  const [dragPosition, setDragPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const fileRefs = useRef({});
  const auth = useSelector((state) => state.auth);
  const role = auth?.profile?.usertype;

  // Only these roles can edit documents
  const canEdit = ['admin', 'callcentersupervisor', 'callcenteragent'].includes(role);
  const L = (key, dflt, opts) => t(key, { defaultValue: dflt, ...(opts || {}) });

  useEffect(() => {
    dispatch(fetchUsersOnce());
  }, [dispatch, fetchUsersOnce]);

  useEffect(() => {
    if (staticusers) {
      const user = staticusers.find((u) => u.id === id.toString());
      if (!user) {
        navigate('/404');
      } else {
        setData(user);
      }
    }
  }, [staticusers, id, navigate]);

  // Fetch secure URLs for PII documents
  useEffect(() => {
    const fetchSecureUrls = async () => {
      if (!data || !data.id || !firebaseAuth || !firebaseAuth.currentUser) return;

      setLoadingUrls(true);
      const urls = {};

      try {
        const idToken = await firebaseAuth.currentUser.getIdToken();

        // List of PII document fields to fetch secure URLs for
        const piiFields = [
          { field: 'verifyIdImage', url: data.verifyIdImage },
          { field: 'licenseImage', url: data.licenseImage },
          { field: 'licenseImageBack', url: data.licenseImageBack },
          { field: 'insuranceImage', url: data.insuranceImage },
        ];

        for (const { field, url } of piiFields) {
          if (url) {
            const storagePath = extractStoragePath(url);
            if (storagePath) {
              const secureUrl = await getSecureDocumentUrl(data.id, storagePath, idToken, store, config);
              if (secureUrl) {
                urls[field] = secureUrl;
              }
            }
          }
        }

        setSecureUrls(urls);
      } catch (error) {
        console.error('Error fetching secure URLs:', error);
      } finally {
        setLoadingUrls(false);
      }
    };

    fetchSecureUrls();
  }, [data, firebaseAuth, config]);

  // Previews of the chosen files (released when they change).
  const previews = useMemo(() => {
    const out = {};
    Object.keys(images).forEach((k) => {
      if (images[k]) out[k] = URL.createObjectURL(images[k]);
    });
    return out;
  }, [images]);
  useEffect(() => () => Object.values(previews).forEach((u) => URL.revokeObjectURL(u)), [previews]);

  const handleImageChange = useCallback(
    (field) => (e) => {
      const file = e.target.files && e.target.files[0];
      if (file) setImages((prev) => ({ ...prev, [field]: file }));
      setFormError(null);
    },
    []
  );

  const handleSaveUser = useCallback(async () => {
    setFormError(null);
    if (!settings.AllowCriticalEditsAdmin) {
      setFormError(t('demo_mode'));
      return;
    }
    const pending = Object.entries(images).filter(([, image]) => image);
    if (pending.length === 0) {
      setFormError(t('no_doc_uploaded'));
      return;
    }
    setLoading(true);
    try {
      // updateLicenseImage uploads the file, writes the user record, and (now)
      // awaits cache coherence + dispatches UPSERT_USER. Awaiting these here
      // refreshes `staticusers` — both this view and the Drivers compliance
      // view — with the REAL uploaded URLs. Replaces the old blind 3s timer +
      // fetchUsersOnce, which raced the upload and could read a stale cache and
      // revert the upload (the bug). Also removes a redundant /users read.
      await Promise.all(pending.map(([field, image]) => dispatch(updateLicenseImage(data?.id, image, field))));
      setImages(NO_IMAGES);
      setEditable(false);
      showToast.success(L('documents_uploaded_successfully', 'Documents uploaded successfully!'));
    } catch (err) {
      setFormError(err?.message || t('no_doc_uploaded'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.AllowCriticalEditsAdmin, images, data?.id, dispatch, t, updateLicenseImage]);

  const handleCancel = useCallback(() => {
    setImages(NO_IMAGES);
    setEditable(false);
    setFormError(null);
  }, []);

  // Use secure URLs if available, fallback to original URLs
  const getDisplayUrl = (field, originalUrl) => secureUrls[field] || originalUrl;

  const commonFields = [{ title: t('verifyid_image'), imageUrl: getDisplayUrl('verifyIdImage', data?.verifyIdImage), field: 'verifyIdImage' }];
  const driverFields = [
    { title: t('license_image_front'), imageUrl: getDisplayUrl('licenseImage', data?.licenseImage), field: 'licenseImage' },
    { title: t('license_image_back'), imageUrl: getDisplayUrl('licenseImageBack', data?.licenseImageBack), field: 'licenseImageBack' },
    { title: t('insurance_image'), imageUrl: getDisplayUrl('insuranceImage', data?.insuranceImage), field: 'insuranceImage' },
  ];
  const imageFields = data?.usertype === 'driver' ? [...commonFields, ...driverFields] : commonFields;

  const handleImageClick = (imageUrl, title) => {
    if (imageUrl) {
      setZoomLevel(1);
      setDragPosition({ x: 0, y: 0 });
      setImageModal({ open: true, url: imageUrl, title });
    }
  };

  const handleCloseModal = () => {
    setImageModal({ open: false, url: '', title: '' });
    setZoomLevel(1);
    setDragPosition({ x: 0, y: 0 });
  };

  const handleZoomIn = () => setZoomLevel((prev) => Math.min(prev + 0.25, MAX_ZOOM));
  const handleZoomOut = () => {
    setZoomLevel((prev) => {
      const newZoom = Math.max(prev - 0.25, MIN_ZOOM);
      if (newZoom <= 1) setDragPosition({ x: 0, y: 0 });
      return newZoom;
    });
  };
  const handleMouseDown = (e) => {
    if (zoomLevel > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - dragPosition.x, y: e.clientY - dragPosition.y });
    }
  };
  const handleMouseMove = (e) => {
    if (isDragging && zoomLevel > 1) {
      setDragPosition({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
    }
  };
  const handleMouseUp = () => setIsDragging(false);
  const handleResetPosition = () => {
    setDragPosition({ x: 0, y: 0 });
    setZoomLevel(1);
  };

  const fullName = data ? `${data.firstName || ''} ${data.lastName || ''}`.trim() : '';
  const frame = { width: '100%', aspectRatio: '4 / 3', borderRadius: '10px', overflow: 'hidden', bgcolor: c.SURFACE_ALT, display: 'flex', alignItems: 'center', justifyContent: 'center' };

  const renderCard = (doc) => {
    const chosen = previews[doc.field];
    return (
      <Paper key={doc.field} component="section" aria-label={doc.title} sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <IconCircle icon={<BadgeOutlinedIcon />} hue="info" size={32} />
          <Typography component="h2" sx={{ fontSize: 15.5, fontWeight: 700, color: c.INK, overflowWrap: 'anywhere' }}>
            {doc.title}
          </Typography>
        </Box>
        {editable ? (
          <ButtonBase
            onClick={() => fileRefs.current[doc.field] && fileRefs.current[doc.field].click()}
            aria-label={`${t('click_to_upload')}: ${doc.title}`}
            sx={{
              ...frame,
              flexDirection: 'column',
              gap: 1,
              border: `2px dashed ${chosen ? c.ACCENT : c.OUTLINE_STRONG}`,
              bgcolor: chosen ? c.WHITE : c.SURFACE_ALT,
              color: c.INK_2,
              '&:hover': { borderColor: c.ACCENT },
              '&:focus-visible': { outline: `2px solid ${c.ACCENT}`, outlineOffset: 2 },
            }}
          >
            {chosen ? (
              <Box component="img" src={chosen} alt={doc.title} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <>
                <FileUploadOutlinedIcon sx={{ fontSize: 40, color: c.ACCENT }} />
                <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{t('click_to_upload')}</Typography>
              </>
            )}
          </ButtonBase>
        ) : loadingUrls && data && data[doc.field] ? (
          <Skeleton variant="rounded" sx={{ ...frame, transform: 'none' }} />
        ) : doc.imageUrl ? (
          <ButtonBase
            onClick={() => handleImageClick(doc.imageUrl, doc.title)}
            aria-label={`${L('zoom_in', 'Zoom in')}: ${doc.title}`}
            sx={{ ...frame, '&:focus-visible': { outline: `2px solid ${c.ACCENT}`, outlineOffset: 2 } }}
          >
            <Box component="img" src={doc.imageUrl} alt={doc.title} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </ButtonBase>
        ) : (
          <Box sx={{ ...frame, flexDirection: 'column', gap: 1, color: c.TEXT_3 }}>
            <ImageNotSupportedOutlinedIcon sx={{ fontSize: 36 }} />
            <Typography sx={{ fontSize: 14 }}>{L('no_document_uploaded', 'Document Not Uploaded')}</Typography>
          </Box>
        )}
        <input
          ref={(el) => {
            fileRefs.current[doc.field] = el;
          }}
          type="file"
          hidden
          onChange={handleImageChange(doc.field)}
        />
      </Paper>
    );
  };

  return (
    <Box dir={i18n.dir()} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <BackButton onClick={() => goBackOr(navigate, `/users/${rId}`, { state: { pageNo: state?.pageNo } })} />
      <PageHeader
        title={calmCaps(t('documents_title'))}
        count={fullName || undefined}
        primaryAction={{ label: t('edit'), icon: <EditOutlinedIcon />, onClick: () => setEditable(true), show: canEdit && !editable && !!data }}
      />

      {!data ? (
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' } }}>
          {[0, 1].map((k) => (
            <Skeleton key={k} variant="rounded" height={280} />
          ))}
        </Box>
      ) : (
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' } }}>
          {imageFields.map(renderCard)}
        </Box>
      )}

      {formError ? <InlineMessage kind="error">{formError}</InlineMessage> : null}

      {editable ? (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 1.5 }}>
          <Button variant="outlined" color="neutral" onClick={handleCancel} disabled={loading}>
            {L('confirm_cancel', 'Cancel')}
          </Button>
          <Button variant="contained" onClick={handleSaveUser} disabled={loading} startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <CheckRoundedIcon />}>
            {loading ? L('form_saving', 'Saving…') : t('save')}
          </Button>
        </Box>
      ) : null}

      {/* A document, large: zoom and drag */}
      <Dialog open={imageModal.open} onClose={handleCloseModal} fullScreen aria-labelledby="document-zoom-title" PaperProps={{ sx: { bgcolor: c.INK, color: c.WHITE } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, px: 2, py: 1.5 }}>
          <Typography id="document-zoom-title" sx={{ fontWeight: 700, fontSize: 18, color: c.WHITE, overflowWrap: 'anywhere' }}>
            {imageModal.title}
          </Typography>
          <IconButton onClick={handleCloseModal} aria-label={L('close_dialog', 'Close')} sx={{ color: c.WHITE }}>
            <CloseRoundedIcon />
          </IconButton>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, pb: 1 }}>
          <IconButton onClick={handleZoomOut} disabled={zoomLevel <= MIN_ZOOM} aria-label={L('zoom_out', 'Zoom out')} sx={{ bgcolor: c.WHITE, '&:hover': { bgcolor: c.SURFACE_ALT }, '&.Mui-disabled': { bgcolor: c.OUTLINE_STRONG } }}>
            <ZoomOutRoundedIcon />
          </IconButton>
          <Typography aria-live="polite" sx={{ color: c.WHITE, minWidth: 64, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>
            {Math.round(zoomLevel * 100)}%
          </Typography>
          <IconButton onClick={handleZoomIn} disabled={zoomLevel >= MAX_ZOOM} aria-label={L('zoom_in', 'Zoom in')} sx={{ bgcolor: c.WHITE, '&:hover': { bgcolor: c.SURFACE_ALT }, '&.Mui-disabled': { bgcolor: c.OUTLINE_STRONG } }}>
            <ZoomInRoundedIcon />
          </IconButton>
          {zoomLevel > 1 ? (
            <Button onClick={handleResetPosition} variant="outlined" sx={{ color: c.WHITE, borderColor: c.WHITE, ml: 1 }}>
              {t('reset')}
            </Button>
          ) : null}
        </Box>
        <Box
          sx={{
            overflow: 'hidden',
            flex: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            cursor: zoomLevel > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
            userSelect: 'none',
            p: 2,
          }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          {imageModal.url ? (
            <Box
              component="img"
              src={imageModal.url}
              alt={imageModal.title}
              draggable={false}
              sx={{
                transform: `scale(${zoomLevel}) translate(${dragPosition.x / zoomLevel}px, ${dragPosition.y / zoomLevel}px)`,
                transformOrigin: 'center center',
                transition: isDragging ? 'none' : 'transform 0.2s ease-in-out',
                maxWidth: '90%',
                maxHeight: '100%',
                objectFit: 'contain',
              }}
            />
          ) : null}
        </Box>
      </Dialog>
    </Box>
  );
}

export default UserDocuments;
